import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ROUTE_PATTERNS } from '@/shared/config/routes';
import { StaggerItem } from '@/shared/ui/animations';
import { EmptyState } from '@/shared/ui/EmptyState';

import {
  AnalysisSectionLayout,
  PROSE_CARD_STAGGER_SLOTS,
  SectionSeparator,
} from '../../components';
import {
  useAnalysisFromContext,
  type AnalysisSectionProps,
} from '../../utils/useAnalysisFromContext';
import type { MarketingPerformanceBodyProps } from './MarketingPerformance.types';
import { AiInsights } from './components/AiInsights';
import { DataFilters } from './components/DataFilters';
import { ExtendedMetrics } from './components/ExtendedMetrics';
import { FunnelKpiCards } from './components/FunnelKpiCards';
import { FunnelMatrix } from './components/FunnelMatrix';
import { MetricTrends } from './components/MetricTrends';
import { ALL_REGIONS, availableMonths, type MarketingPerformanceFilters } from './model/filters';
import { useMarketingPerformanceData } from './model/useMarketingPerformanceData';
import { parseYougovData } from './model/yougovData.schema';

// This page has no leading highlight card, but the offset keeps its cascade on
// the same rhythm as sections that have one.
const CARD_SETTLE_GAP = 1;
const sectionStagger = (order: number) => PROSE_CARD_STAGGER_SLOTS + CARD_SETTLE_GAP + 1 + order;

function defaultFilters(months: string[]): MarketingPerformanceFilters {
  return { from: months[0] ?? '', to: months[months.length - 1] ?? '', region: ALL_REGIONS };
}

const MarketingPerformanceBody = ({ data, exporting }: MarketingPerformanceBodyProps) => {
  const { t } = useTranslation('analysis');
  const months = useMemo(() => availableMonths(data.monthly), [data]);
  const [filters, setFilters] = useState<MarketingPerformanceFilters>(defaultFilters(months));
  // `onApply` normalises an inverted from/to pair, so the normalised result can equal
  // the already-applied value with nothing to key a remount off. Bumping this on every
  // apply and clear forces DataFilters to re-seed its pending state regardless.
  const [applyCount, setApplyCount] = useState(0);
  const page = useMarketingPerformanceData(data, filters);

  // AiInsights is generated once at upload over the whole file, so it stays visible
  // even when the current filter matches zero rows. It's the only block that can
  // render null today (a failed upload persists insights: null); its separator is
  // guarded on that presence so it never dangles.
  const hasInsights = Boolean(data.insights);

  return (
    <div className="flex flex-col gap-10 pb-4">
      <StaggerItem index={sectionStagger(0)} variant="swipeUp">
        <FunnelKpiCards kpis={page.kpis} />
      </StaggerItem>
      {!exporting && (
        <StaggerItem index={sectionStagger(1)} variant="swipeUp">
          <DataFilters
            months={months}
            availableRegions={data.availableRegions}
            value={filters}
            applyCount={applyCount}
            onApply={(next) => {
              setApplyCount((count) => count + 1);
              setFilters(next);
            }}
            onClear={() => {
              setApplyCount((count) => count + 1);
              setFilters(defaultFilters(months));
            }}
          />
        </StaggerItem>
      )}
      <SectionSeparator />
      {hasInsights && (
        <StaggerItem index={sectionStagger(2)} variant="swipeUp">
          <AiInsights insights={data.insights} />
        </StaggerItem>
      )}
      {hasInsights && <SectionSeparator />}
      {page.filteredRows.length === 0 ? (
        <EmptyState
          title={t('analysisResult.marketingPerformance.noDataForFilters')}
          description={t('analysisResult.marketingPerformance.noDataForFiltersDescription')}
          height="100%"
        />
      ) : (
        <>
          <StaggerItem index={sectionStagger(3)} variant="swipeUp">
            <FunnelMatrix funnel={page.funnel} />
          </StaggerItem>

          <SectionSeparator />
          <StaggerItem index={sectionStagger(4)} variant="swipeUp">
            <ExtendedMetrics
              rows={page.filteredRows}
              brands={page.orderedBrands}
              targetBrand={page.targetBrand}
              exporting={exporting}
            />
          </StaggerItem>

          <SectionSeparator />
          {/* MetricTrends intentionally still renders only its default metric on export —
              charting all 15 metrics as separate exported charts would balloon the PDF. */}
          <StaggerItem index={sectionStagger(5)} variant="swipeUp">
            <MetricTrends rows={page.filteredRows} brands={page.orderedBrands} />
          </StaggerItem>
        </>
      )}
    </div>
  );
};

export const MarketingPerformance = ({
  analysis: analysisProp,
  exporting,
}: AnalysisSectionProps = {}) => {
  const { t } = useTranslation('analysis');
  const analysis = useAnalysisFromContext(analysisProp);
  const parsed = useMemo(() => parseYougovData(analysis.yougovData), [analysis.yougovData]);
  // An upload whose dates never parsed still persists a schema-valid blob with
  // `monthly: []` — to the user that is indistinguishable from no upload at all,
  // so it gets the same empty state rather than an unfilterable "0 of 0" range.
  const hasNoData =
    parsed.status === 'absent' || (parsed.status === 'ok' && parsed.data.monthly.length === 0);

  return (
    <AnalysisSectionLayout
      analysisId={analysis.id}
      brand={analysis.brandName}
      section={ROUTE_PATTERNS.IQ_SECTION_MARKETING_PERFORMANCE}
      title={t('analysisResult.marketingPerformance.marketingPerformance')}
      subtitle={t('analysisResult.marketingPerformance.subtitle', {
        brandName: analysis.brandName,
      })}
      exporting={exporting}
    >
      {hasNoData && (
        <EmptyState
          title={t('analysisResult.marketingPerformance.noYougovData')}
          description={t('analysisResult.marketingPerformance.noYougovDataDescription')}
          height="100%"
        />
      )}
      {parsed.status === 'invalid' && (
        <EmptyState
          title={t('analysisResult.marketingPerformance.unreadableData')}
          description={t('analysisResult.marketingPerformance.unreadableDataDescription')}
          height="100%"
        />
      )}
      {parsed.status === 'ok' && !hasNoData && (
        <MarketingPerformanceBody key={analysis.id} data={parsed.data} exporting={exporting} />
      )}
    </AnalysisSectionLayout>
  );
};
